import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NO_DECIMAL_PLACES, NumberUtils } from '@modeso/aumico-lib-core-fe';
import { IFinancialStatementImportDefinition } from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import BigNumber from 'bignumber.js';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { FinancialStatementImportDefinition } from '../../enums/financialStatementImportDefinition.enum';
import { SpreadSheetPreviewHelper } from '../../helpers/spreadsheet-preview.helper';
import { IFinancialStatementDefinitionFormModel } from '../../interfaces/IFinancialStatementDefinitionFormModel';
import { ISheet } from '../../interfaces/ISheet';
import { ISheetRow } from '../../interfaces/ISheetRow';
import { ISpreadSheet } from '../../interfaces/ISpreadSheet';
import { ISpreadSheetClick } from '../../interfaces/ISpreadSheetClick.interface';

const debug = Debug('aumico:aumico-lib-annualreport-fe:FinancialStatementImportDefinitionComponent');
@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-financial-statement-import-definition-component',
    templateUrl: 'financial-statement-import-definition.component.html',
    styleUrls: ['financial-statement-import-definition.component.scss'],
})
export class FinancialStatementImportDefinitionComponent implements OnInit, OnChanges {
    @Input() title: string;

    copiedDefinition: IFinancialStatementImportDefinition;
    @Input() set definitionToCopy(value: IFinancialStatementImportDefinition) {
        this.copiedDefinition = cloneDeep<IFinancialStatementImportDefinition>(value);
    }

    private currentFilledImportDefinition: IFinancialStatementImportDefinition | undefined;
    @Input() set currentFilledData(value: IFinancialStatementImportDefinition) {
        this.currentFilledImportDefinition = cloneDeep<IFinancialStatementImportDefinition>(value);
    }

    private importDefinitionData: IFinancialStatementImportDefinition | undefined;
    @Input() set importDefinition(value: IFinancialStatementImportDefinition) {
        this.importDefinitionData = cloneDeep<IFinancialStatementImportDefinition>(value);
        if (this.spreadSheetData != null) {
            // // let's check if input data matches the newly uploaded file
            const isPreFillDataValid = this.isPreFillDataValid();
            if (isPreFillDataValid) {
                this.applyPreFill();
            }
            // // // the copy option is only visible if prefill won't apply
            if (this.copiedDefinition != null && !isPreFillDataValid) {
                this.showForm = false;
            } else {
                this.fillManually();
            }
        }
    }

    @Input() set spreadSheetClickData(value: ISpreadSheetClick) {
        if (value != null) {
            this.setCorrespondingClickValueInForm(value);
        }
    }

    private spreadSheetData: ISpreadSheet;
    @Input() set data(value: ISpreadSheet) {
        this.spreadSheetData = cloneDeep<ISpreadSheet>(value);
        this.sheetNameOptions = this.getSheetNameOptions();
        this.initImportDefinitionForm();
        if (this.importDefinitionData != null) {
            // let's check if input data matches the newly uploaded file
            const isPreFillDataValid = this.isPreFillDataValid();

            if (isPreFillDataValid) {
                this.applyPreFill();
            }
            // the copy option is only visible if prefill won't apply
            if (this.copiedDefinition != null && !isPreFillDataValid) {
                this.showForm = false;
            } else {
                this.fillManually();
            }
        }
    }

    @Input() set autoFillApplied(value: boolean) {
        if (value && this.importDefinitionData != null) {
            this.initImportDefinitionForm();
            // let's check if autofill data matches the newly uploaded file
            if (this.isPreFillDataValid()) {
                this.applyPreFill();
            }
        }

        // the copy option is only visible if prefill won't apply
        if (this.copiedDefinition != null && !this.isPreFillDataValid()) {
            this.showForm = false;
        } else {
            this.fillManually();
        }
    }
    showPreviousYearField = true;
    private _noPreviousYearData: boolean;
    @Input() set noPreviousYearData(value: boolean) {
        this._noPreviousYearData = value;

        this.showPreviousYearFormIfNeeded();

    }

    private _priorYearUnmatchableAccountsDetected: boolean = false;
    @Input() set priorYearUnmatchableAccountsDetected(value: boolean) {
        this._priorYearUnmatchableAccountsDetected = value;
        this.showPreviousYearFormIfNeeded();

    }

    private showPreviousYearFormIfNeeded() {
        if (this._priorYearUnmatchableAccountsDetected) {
            // Requirement 3: If unmatchable accounts are detected, always SHOW the field.
            this.showPreviousYearField = true;
            this.form.controls['previousYearColNo'].clearValidators();
            this.form.controls['previousYearColNo'].setErrors(null);
        } else if (this._noPreviousYearData || this._isPreviousYearDataTakenFromPreviousAnnualReport) {
            // Requirements 1 & 2: If either of these flags is true, HIDE the field.
            // This block is only reached if _priorYearUnmatchableAccountsDetected is false.
            this.showPreviousYearField = false;
            this.form.controls['previousYearColNo'].clearValidators();
            this.form.controls['previousYearColNo'].setErrors(null);
        } else {
            // The ELSE block: None of the three flags are true.
            // Based on the first two requirements (only hide if they are true),
            // if all are false, it should default to displaying (SHOW).
            // Let's assume the default behavior when none of the conditions are met is to SHOW.
            this.showPreviousYearField = true;
            this.form.controls['previousYearColNo'].setValidators(Validators.required);
            this.form.controls['previousYearColNo'].setErrors(null);
        }
    }


    private _isPreviousYearDataTakenFromPreviousAnnualReport: boolean = false;
    @Input() set isPreviousYearDataTakenFromPreviousAnnualReport(value: boolean) {
        this._isPreviousYearDataTakenFromPreviousAnnualReport = value;
        this.showPreviousYearFormIfNeeded();
    }

    showShareholderField = false;
    @Input() set showShareholderInformation(value: boolean) {
        this.showShareholderField = value;
        if (value) {
            this.form.controls['shareholderColNo'].setValidators(Validators.required);
        }
    }

    form: UntypedFormGroup;
    columnOptions: INameId[] = [];
    sheetNameOptions: INameId[] = [];
    private initialFormData: IFinancialStatementDefinitionFormModel = {} as IFinancialStatementDefinitionFormModel;
    activeField: FinancialStatementImportDefinition | null = FinancialStatementImportDefinition.sheetName;
    showForm: boolean;
    showTransferEntries: boolean;
    accountsStartingRow: number | null | undefined;
    private sheetName: string | undefined;
    // flags for first time
    sheetNameFirstTime = true;
    accountsStartingRowFirstTime = true;
    accountNoFirstTime = true;
    accountNameFirstTime = true;
    balanceFirstTime = true;
    private previousYearFirstTime = true;
    shareholderFirstTime = true;

    fieldsEnum = FinancialStatementImportDefinition;

    private formValueChanges: Subscription | undefined;
    private sheetNameValueChange: Subscription | undefined;

    @Output() importDefinitionCompleted: EventEmitter<IFinancialStatementImportDefinition | boolean> =
        new EventEmitter();

    constructor(
        private formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
    ) {
        this.matIconRegistry.addSvgIcon(
            'interface-delete 1',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/interface-delete 1.svg`),
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes != null && changes['data'] != null) {
            // if the spread sheet data changes then we need to set sheetName back to null
            this.sheetName = undefined;
        }
    }

    ngOnInit(): void {
        debug('ngOnInit');
        // on initialization, we need to check if user entered before any data to be used again(autofilled)
        if (this.currentFilledImportDefinition != null) {
            this.importDefinitionData = cloneDeep<IFinancialStatementImportDefinition>(
                this.currentFilledImportDefinition,
            );
            if (this.isPreFillDataValid()) {
                this.applyPreFill();
            }
        }
        this.checkFormCompletion();
    }

    private applyPreFill(): void {
        this.activeField = null;
        this.sheetName = this.importDefinitionData.sheetName.name;
        this.accountsStartingRow = this.getAccountsStartingRowIfValid(this.importDefinitionData.accountsStartingRow);
        this.setColumnSelectionOptions();
        this.form.controls['sheetName'].setValue(
            this.sheetNameOptions.find((option) => option.name === this.importDefinitionData.sheetName.name),
            { emitEvent: false },
        );
        this.form.controls['accountsStartingRow'].setValue(this.accountsStartingRow, { emitEvent: false });
        this.form.controls['accountNameCol'].setValue(
            this.getSelectionOption(this.importDefinitionData.accountNameCol),
            { emitEvent: false },
        );
        this.form.controls['accountNoCol'].setValue(this.getSelectionOption(this.importDefinitionData.accountNoCol), {
            emitEvent: false,
        });
        this.form.controls['balanceColNo'].setValue(this.getSelectionOption(this.importDefinitionData.balanceColNo), {
            emitEvent: false,
        });
        this.form.controls['previousYearColNo'].setValue(
            this.getSelectionOption(this.importDefinitionData.previousYearColNo),
            { emitEvent: false },
        );
        this.form.controls['shareholderColNo'].setValue(
            this.getSelectionOption(this.importDefinitionData.shareholderColNo),
            { emitEvent: false },
        );
        this.sheetNameFirstTime = false;
        this.accountsStartingRowFirstTime = false;
        this.accountNoFirstTime = false;
        this.accountNameFirstTime = false;
        this.balanceFirstTime = false;
        this.previousYearFirstTime = false;
        this.shareholderFirstTime = false;
        this.checkFormCompletion();
    }

    private isPreFillDataValid(): boolean {
        // we check preFill validity by getting the same sheet name
        let isPreFillValid = false;
        if (this.importDefinitionData != null) {
            const isSameSheetNameFound = this.spreadSheetData.sheets.find(
                (sheet) => sheet.name === this.importDefinitionData.sheetName.name,
            );
            if (isSameSheetNameFound != null) {
                isPreFillValid = true;
            }
        }
        return isPreFillValid;
    }

    private onFormValueChange(): void {
        if (this.formValueChanges != null) {
            this.formValueChanges.unsubscribe();
        }

        this.formValueChanges = this.form.valueChanges
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((formValue: IFinancialStatementImportDefinition) => {
                this.checkFirstTime(formValue);
                this.checkFormCompletion();
                if (formValue.sheetName != null) {
                    this.sheetName = formValue.sheetName.name;
                    // check that the following fields have not been selected yet in order to show transfer entries button
                    if (
                        formValue.accountNameCol == null &&
                        formValue.accountNoCol == null &&
                        formValue.accountsStartingRow == null &&
                        formValue.balanceColNo == null &&
                        formValue.previousYearColNo == null &&
                        formValue.shareholderColNo == null
                    ) {
                        this.showTransferEntries = true;
                    }
                } else {
                    this.showTransferEntries = false;
                }
            });
    }

    private checkFormCompletion(): void {
        if (this.form.valid) {
            debug('form value', this.form);
            this.importDefinitionCompleted.emit(this.form.value);
            this.setActiveField(null);
        } else {
            this.importDefinitionCompleted.emit(false);
        }
    }

    private setCorrespondingClickValueInForm(clickData: ISpreadSheetClick) {
        if (this.form != null && this.activeField != null) {
            if (
                this.activeField === FinancialStatementImportDefinition.accountsStartingRow &&
                !isNaN(clickData.rowNo)
            ) {
                this.form.controls['accountsStartingRow'].setValue(clickData.rowNo);
            } else if (this.activeField === FinancialStatementImportDefinition.sheetName) {
                this.form.controls['sheetName'].setValue(
                    this.sheetNameOptions.find((sheet) => sheet.name === clickData.sheetName),
                );
            } else {
                // because if a sheet name tab is clicked we don't want to change the active field value
                if (clickData.rowNo != null && clickData.colNo != null) {
                    this.form.controls[this.activeField].setValue(
                        this.columnOptions.find((option) => +option.id === clickData.colNo),
                    );
                }
            }
        }
    }

    private onSheetNameChange(): void {
        if (this.sheetNameValueChange != null) {
            this.sheetNameValueChange.unsubscribe();
        }

        this.sheetNameValueChange = this.form
            .get(FinancialStatementImportDefinition.sheetName)
            .valueChanges.pipe(
                distinctUntilChanged((previousSheetNameChange, currentSheetNameChange) =>
                    fastDeepEqual(previousSheetNameChange, currentSheetNameChange),
                ),
                untilDestroyed(this),
            )
            .subscribe((sheet: INameId) => {
                this.form.controls['accountsStartingRow'].reset();
                this.resetImportDefinitionFields();
                this.sheetName = sheet != null ? sheet.name : undefined;
                this.setColumnSelectionOptions();
            });
    }

    private resetImportDefinitionFields(): void {
        this.form.controls['accountNoCol'].reset();
        this.form.controls['accountNameCol'].reset();
        this.form.controls['balanceColNo'].reset();
        this.form.controls['previousYearColNo'].reset();
        this.form.controls['shareholderColNo'].reset();
    }

    private initImportDefinitionForm(): void {
        this.form = this.formBuilder.group({
            sheetName: new UntypedFormControl(null, [Validators.required]),
            accountsStartingRow: new UntypedFormControl(null, [Validators.required]),
            accountNameCol: new UntypedFormControl(null, [Validators.required]),
            accountNoCol: new UntypedFormControl(null, [Validators.required]),
            balanceColNo: new UntypedFormControl(null, [Validators.required]),
            previousYearColNo: new UntypedFormControl(null, this.showPreviousYearField ? [Validators.required] : []),
            shareholderColNo: new UntypedFormControl(null, this.showShareholderField ? [Validators.required] : []),
        });
        this.onFormValueChange();
        this.onSheetNameChange();
    }

    private getSheetNameOptions(): INameId[] {
        const sheetNameOptions: INameId[] = [];
        if (this.spreadSheetData) {
            const options: string[] = this.spreadSheetData.sheets.map((sheet: ISheet) => sheet.name);
            options.forEach((option: string, index: number) => {
                sheetNameOptions.push({
                    id: index.toString(),
                    name: option,
                });
            });
        }
        return sheetNameOptions;
    }

    private setColumnSelectionOptions(): void {
        this.columnOptions = [];
        if (this.sheetName != null && this.sheetName.trim().length > 0) {
            const sheet = this.spreadSheetData.sheets.find((sheet: ISheet) => sheet.name === this.sheetName);
            const rows = sheet.rows.filter((row) => row != null);
            const selectionsLength = this.getDropDownSelectionsLength(rows);
            const columnLetters: string[] = [];
            for (let index = 1; index < selectionsLength + 1; index++) {
                const columnLetter = SpreadSheetPreviewHelper.getColumnNameFromColumnNumber(index);
                columnLetters.push(columnLetter);
            }
            columnLetters.forEach((letter, index) => {
                this.columnOptions.push({
                    // the id in selection option represents the column number

                    // this plus 2 is for:
                    // a. not make it 0-based
                    // b. to take account for the number column
                    id: (index + 2).toString(),
                    name: letter,
                });
            });
        }
    }
    // definition to copy is given in column numbers and we need the cell content (name) so we do a mapping
    private mapDefinitionToCopyToFormData(): IFinancialStatementDefinitionFormModel {
        const formData: IFinancialStatementDefinitionFormModel = {} as IFinancialStatementDefinitionFormModel;
        formData.accountsStartingRow = this.getAccountsStartingRowIfValid(this.copiedDefinition.accountsStartingRow);
        formData.accountNoCol = this.copiedDefinition.accountNoCol.name;
        formData.accountNameCol = this.copiedDefinition.accountNameCol.name;
        formData.balanceColNo = this.copiedDefinition.balanceColNo.name;
        formData.previousYearColNo = this.showPreviousYearField ? this.copiedDefinition.previousYearColNo.name : null;
        formData.shareholderColNo = this.showShareholderField ? this.copiedDefinition.shareholderColNo.name : null;

        return formData;
    }

    transferEntries(): void {
        this.sheetName = this.form.get('sheetName')?.value.name as string | undefined;
        this.showForm = true;
        this.showTransferEntries = false;
        // the first time flags are false in the case of copying data
        this.sheetNameFirstTime = false;
        this.accountsStartingRowFirstTime = false;
        this.accountNoFirstTime = false;
        this.accountNameFirstTime = false;
        this.balanceFirstTime = false;
        this.previousYearFirstTime = false;
        this.shareholderFirstTime = false;
        this.initialFormData = this.mapDefinitionToCopyToFormData();
        this.setDefinitionToCopyValues();
        this.activeField = null;
    }

    private setDefinitionToCopyValues(): void {
        let accountsStartingRow: number | null = null;
        if (NumberUtils.isFinite(this.initialFormData.accountsStartingRow)) {
            accountsStartingRow = NumberUtils.roundNumber(
                this.initialFormData.accountsStartingRow,
                false,
                NO_DECIMAL_PLACES,
                BigNumber.ROUND_DOWN,
            );
        }

        this.form.controls['accountsStartingRow'].setValue(accountsStartingRow);
        this.accountsStartingRow = accountsStartingRow;

        this.setColumnSelectionOptions();
        this.form.controls['accountNoCol'].setValue(
            this.columnOptions.find((option: INameId) => option.name === this.initialFormData.accountNoCol),
        );

        this.form.controls['accountNameCol'].setValue(
            this.columnOptions.find((option: INameId) => option.name === this.initialFormData.accountNameCol),
        );

        this.form.controls['balanceColNo'].setValue(
            this.columnOptions.find((option: INameId) => option.name === this.initialFormData.balanceColNo),
        );

        this.form.controls['previousYearColNo'].setValue(
            this.columnOptions.find((option: INameId) => option.name === this.initialFormData.previousYearColNo),
        );

        this.form.controls['shareholderColNo'].setValue(
            this.columnOptions.find((option: INameId) => option.name === this.initialFormData.shareholderColNo),
        );
    }

    fillManually(): void {
        this.showForm = true;
        this.showTransferEntries = false;
        if (this.copiedDefinition !== null && !this.sheetNameFirstTime) {
            this.setActiveField(FinancialStatementImportDefinition.accountsStartingRow);
        }
    }

    // checks if this is the first time the field appears, because in this scenario
    // we need to automatically set active fields without click from user
    private checkFirstTime(formValue: IFinancialStatementImportDefinition): void {
        if (formValue.sheetName && this.sheetNameFirstTime) {
            this.sheetNameFirstTime = false;
            // if this is the first time for a field, then once it has a value the active field is automatically moved to the next one
            if (this.showForm) {
                this.setActiveField(FinancialStatementImportDefinition.accountsStartingRow);
            }
        }
        if (formValue.accountsStartingRow && this.accountsStartingRowFirstTime) {
            this.accountsStartingRowFirstTime = false;
            this.setActiveField(FinancialStatementImportDefinition.accountNo);
        }
        if (formValue.accountNoCol != null && this.accountNoFirstTime) {
            this.accountNoFirstTime = false;
            this.setActiveField(FinancialStatementImportDefinition.accountName);
        }
        if (formValue.accountNameCol != null && this.accountNameFirstTime) {
            this.accountNameFirstTime = false;
            this.setActiveField(FinancialStatementImportDefinition.balance);
        }
        if (formValue.balanceColNo != null && this.balanceFirstTime) {
            this.balanceFirstTime = false;
            if (this.showPreviousYearField) {
                this.setActiveField(FinancialStatementImportDefinition.previousYear);
            } else if (this.showShareholderField) {
                this.setActiveField(FinancialStatementImportDefinition.shareholder);
            }
        }
        if (formValue.previousYearColNo != null && this.previousYearFirstTime) {
            this.previousYearFirstTime = false;
            if (this.showShareholderField) {
                this.setActiveField(FinancialStatementImportDefinition.shareholder);
            } else {
                this.setActiveField(null);
            }
        }
        if (formValue.shareholderColNo != null && this.shareholderFirstTime) {
            this.shareholderFirstTime = false;
            this.setActiveField(null);
        }
    }

    private getAccountsStartingRowIfValid(accountsStartingRow: number): number | null {
        if (this.sheetName == null) {
            return null;
        }

        const selectedSheet = this.spreadSheetData.sheets.find((sheet) => this.sheetName === sheet.name);

        if (selectedSheet == null) {
            return null;
        }

        const numberOfRowsOfCurrentSheet = selectedSheet.rows.length;

        if (numberOfRowsOfCurrentSheet >= accountsStartingRow) {
            return accountsStartingRow;
        }
        return null;
    }

    private getSelectionOption(value: INameId): INameId | null {
        if (value == null) {
            return null;
        }
        const foundSelectionOption = this.columnOptions.find((option) => option.id === value.id);
        if (foundSelectionOption != null) {
            return foundSelectionOption;
        }

        return null;
    }

    setActiveField(activeField: FinancialStatementImportDefinition | null): void {
        debug('activeField', activeField);
        this.activeField = activeField;
    }

    private getDropDownSelectionsLength(rows: ISheetRow[]): number {
        const rowsLengths = rows.map((row) => row.cells.length);
        const maxLength = Math.max(...rowsLengths);
        if (maxLength != null) {
            return maxLength;
        }
        return 0;
    }
}
